import {
  PORTFOLIO_COMPARISON_REASON_LABELS,
  PORTFOLIO_COMPARISON_REASON_OBJ,
  PORTFOLIO_COMPARISON_TASK_KIND_OBJ,
  PORTFOLIO_INSTRUMENT_KIND_OBJ,
  PORTFOLIO_PRICE_SYNC_STATUS_OBJ,
  type PortfolioComparisonManualConfirmation,
  type PortfolioComparisonSyncStatus,
  type PortfolioComparisonTask,
  type PortfolioInstrumentRef,
  type TPortfolioComparisonReason,
  type TPortfolioInstrumentPriceSource,
} from "@signals/shared";
import { formatPortfolioInstrumentName, getPositionInstrument } from "./portfolio-instrument";
import type { PortfolioCompareMover, PortfolioCompareResponse, Position } from "./portfolio.types";

export interface ComparisonReasonView {
  key: string;
  code: TPortfolioComparisonReason;
  label: string;
  message: string | null;
  date: string | null;
  accountIds: string[];
  source: TPortfolioInstrumentPriceSource | null;
  priceDate: string | null;
  manualConfirmation: PortfolioComparisonManualConfirmation | null;
}
export interface ComparisonAccountView {
  key: string;
  accountId: string | null;
  accountName: string;
  position: Position;
}
export interface ComparisonDateView {
  date: string;
  accounts: ComparisonAccountView[];
  reasons: ComparisonReasonView[];
  tasks: PortfolioComparisonTask[];
}
export interface ComparisonInstrumentView {
  key: string;
  instrument: PortfolioInstrumentRef;
  label: string;
  name: string | null;
  from: ComparisonDateView;
  to: ComparisonDateView;
}
export interface PortfolioComparisonView {
  instruments: ComparisonInstrumentView[];
  fxTasks: PortfolioComparisonTask[];
  undatedReasons: ComparisonReasonView[];
  accountNames: ReadonlyMap<string, string>;
}

export function comparisonInstrumentKey(instrument: PortfolioInstrumentRef): string {
  return JSON.stringify([instrument.kind, instrument.kind === PORTFOLIO_INSTRUMENT_KIND_OBJ.OPTION
    ? instrument.optionContractId : instrument.symbolId]);
}
function humanLabel(position: Position, instrument: PortfolioInstrumentRef): string {
  const label = formatPortfolioInstrumentName(position);
  const id = instrument.kind === PORTFOLIO_INSTRUMENT_KIND_OBJ.OPTION ? instrument.optionContractId : instrument.symbolId;
  if (label !== "—" && label !== id) return label;
  return position.symbolName ?? position.instrumentName ?? (instrument.kind === PORTFOLIO_INSTRUMENT_KIND_OBJ.OPTION
    ? "Опционный контракт" : "Ценная бумага");
}
function emptyDate(date: string): ComparisonDateView {
  return { date, accounts: [], reasons: [], tasks: [] };
}
function addReason(list: ComparisonReasonView[], input: Omit<ComparisonReasonView, "key" | "label">): void {
  // Identical explanations are shared across accounts; scope remains explicit in the final key.
  const same = list.find((reason) => reason.code === input.code && reason.date === input.date
    && reason.message === input.message && reason.source === input.source && reason.priceDate === input.priceDate
    && JSON.stringify(reason.manualConfirmation) === JSON.stringify(input.manualConfirmation));
  if (same) {
    same.accountIds = [...new Set([...same.accountIds, ...input.accountIds])].sort();
    same.key = reasonKey(same);
    return;
  }
  const label = PORTFOLIO_COMPARISON_REASON_LABELS[input.code]?.["ru-RU"];
  const reason = { ...input, accountIds: [...new Set(input.accountIds)].sort(),
    message: input.message ?? (label ? null : String(input.code)),
    label: label ?? "Причина недоступности данных", key: "" };
  reason.key = reasonKey(reason);
  list.push(reason);
}
function reasonKey(reason: Omit<ComparisonReasonView, "key" | "label">): string {
  return JSON.stringify([reason.date, reason.accountIds, reason.source, reason.code, reason.message,
    reason.priceDate, reason.manualConfirmation]);
}

/** Presentation only: never sums positions, converts currency, or derives a portfolio return. */
export function buildPortfolioComparisonView(
  data: PortfolioCompareResponse,
  range: { from: string; to: string },
  operation: PortfolioComparisonSyncStatus | null = data.preparation?.operation ?? null,
): PortfolioComparisonView {
  const groups = new Map<string, ComparisonInstrumentView>();
  const accountNames = new Map<string, string>();
  const ensure = (instrument: PortfolioInstrumentRef, position?: Position) => {
    const key = comparisonInstrumentKey(instrument);
    let group = groups.get(key);
    if (!group) {
      group = { key, instrument, label: position ? humanLabel(position, instrument)
        : instrument.kind === PORTFOLIO_INSTRUMENT_KIND_OBJ.OPTION ? "Опционный контракт" : "Ценная бумага",
      name: position?.symbolName ?? position?.instrumentName ?? null, from: emptyDate(range.from), to: emptyDate(range.to) };
      groups.set(key, group);
    } else if (position) {
      group.label = humanLabel(position, instrument);
      group.name = position.symbolName ?? position.instrumentName ?? group.name;
    }
    return group;
  };
  for (const side of ["from", "to"] as const) {
    for (const position of data.boundaries?.[side].positions ?? []) {
      const instrument = getPositionInstrument(position);
      if (!instrument) continue;
      const group = ensure(instrument, position);
      const accountId = position.accountId ?? null;
      const accountName = position.accountName ?? (accountId ? "Счёт без названия" : "Счёт не указан");
      if (accountId) accountNames.set(accountId, accountName);
      const key = JSON.stringify([group.key, group[side].date, accountId]);
      if (!group[side].accounts.some((account) => account.key === key)) {
        group[side].accounts.push({ key, accountId, accountName, position });
      }
      const assessments = position.financialAvailability?.metrics;
      for (const assessment of assessments ? Object.values(assessments) : []) {
        for (const reason of assessment.reasons) addReason(group[side].reasons, {
          code: reason.code, date: reason.date ?? group[side].date,
          accountIds: reason.accountId ? [reason.accountId] : accountId ? [accountId] : [],
          source: position.marketPriceSource ?? null, priceDate: position.marketPriceDate ?? null,
          message: null, manualConfirmation: null,
        });
      }
      for (const missing of position.missingValuations ?? []) if (missing.reason) addReason(group[side].reasons, {
        code: missing.reason, date: missing.date ?? group[side].date,
        accountIds: missing.accountId ? [missing.accountId] : accountId ? [accountId] : [],
        source: position.marketPriceSource ?? null, priceDate: position.marketPriceDate ?? null,
        message: null, manualConfirmation: null,
      });
    }
  }
  const undatedReasons: ComparisonReasonView[] = [];
  for (const missing of data.missingValuations ?? []) {
    if (!missing.reason) continue;
    const instrument = missing.instrument ?? (missing.symbolId
      ? { kind: PORTFOLIO_INSTRUMENT_KIND_OBJ.SECURITY, symbolId: missing.symbolId } : null);
    const group = instrument ? groups.get(comparisonInstrumentKey(instrument))
      ?? ensure(instrument, { symbolTicker: missing.symbol, symbolName: missing.name }) : null;
    const cell = missing.date === range.from ? group?.from : missing.date === range.to ? group?.to : null;
    addReason(cell?.reasons ?? undatedReasons, { code: missing.reason, date: missing.date,
      accountIds: missing.accountId ? [missing.accountId] : [], source: null, priceDate: null,
      message: null, manualConfirmation: null });
  }
  const fxTasks: PortfolioComparisonTask[] = [];
  for (const task of operation?.tasks ?? []) {
    if (task.need.kind === PORTFOLIO_COMPARISON_TASK_KIND_OBJ.FX) { fxTasks.push(task); continue; }
    const group = ensure(task.need.instrument);
    const cell = task.need.targetDate === range.from ? group.from : task.need.targetDate === range.to ? group.to : null;
    if (!cell) continue;
    cell.tasks.push(task);
    for (const reason of task.result?.reasons ?? []) addReason(cell.reasons, {
      code: reason.code, date: reason.targetDate, accountIds: reason.accountIds.length ? reason.accountIds : task.need.accountIds,
      source: reason.source ?? task.need.source, priceDate: reason.priceDate ?? task.result?.priceDate ?? null,
      message: reason.message || null, manualConfirmation: reason.manualConfirmation ?? null,
    });
    if (!task.result && (task.status === PORTFOLIO_PRICE_SYNC_STATUS_OBJ.QUEUED || task.status === PORTFOLIO_PRICE_SYNC_STATUS_OBJ.RUNNING)) {
      addReason(cell.reasons, { code: task.status === PORTFOLIO_PRICE_SYNC_STATUS_OBJ.RUNNING
        ? PORTFOLIO_COMPARISON_REASON_OBJ.LOADING : PORTFOLIO_COMPARISON_REASON_OBJ.NOT_REQUESTED,
      date: task.need.targetDate, accountIds: task.need.accountIds, source: task.need.source,
      priceDate: null, message: null, manualConfirmation: null });
    }
  }
  return { instruments: [...groups.values()].sort((a, b) => a.label.localeCompare(b.label) || a.key.localeCompare(b.key)),
    fxTasks, undatedReasons, accountNames };
}

export function labelPortfolioComparisonMovers(movers: readonly PortfolioCompareMover[], view: PortfolioComparisonView) {
  return movers.map((mover) => {
    const instrument = getPositionInstrument(mover);
    const group = instrument ? view.instruments.find((item) => item.key === comparisonInstrumentKey(instrument)) : null;
    return { ...mover, rowKey: JSON.stringify([instrument ? comparisonInstrumentKey(instrument) : null, mover.accountId]),
      instrumentLabel: group?.label ?? "Инструмент без названия",
      accountLabel: mover.accountId ? view.accountNames.get(mover.accountId) ?? "Счёт без названия" : "Счёт не указан" };
  });
}

export interface PortfolioComparisonRange { from: string; to: string }
function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
export function readPortfolioComparisonRange(params: URLSearchParams): PortfolioComparisonRange | null {
  const from = params.get("compareFrom") ?? "", to = params.get("compareTo") ?? "";
  return isCalendarDate(from) && isCalendarDate(to) && from <= to ? { from, to } : null;
}
export function writePortfolioComparisonRange(params: URLSearchParams, range: PortfolioComparisonRange | null): URLSearchParams {
  const result = new URLSearchParams(params);
  if (range) { result.set("compareFrom", range.from); result.set("compareTo", range.to); }
  else { result.delete("compareFrom"); result.delete("compareTo"); }
  return result;
}
